import { emitHintSignal } from '@flobi/asset-library/hints';
import { selectTool } from './select';
import {
  flattened,
  outlineIn,
  subpathsOfCommands,
  applyToPoint,
  cloneCount,
  clonerPlacements,
  clonerSpec,
  type ClonerMode,
} from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import { clonerEffectors } from '@flobi/artwork-core';
import type { MenuEntry } from '../../ui/menu';
import type { Overlay, Point, RailButton, RailField, Tool, ToolContext, Well } from './types';
import { APPLY, CHANNELS, RECIPES, WHERE, applyOf, numberIn, percentIn, whereOf, type Recipe } from './effectors';
import { addVariation, makeLayers, ownerOf as findOwner, removeVariation, stopRepeating, wrapInCloner } from './repeat';

/**
 * The Cloner: one thing, many times, still one thing.
 *
 * Cinema 4D's MoGraph cloner, which is three stages — work out where the copies go, let
 * effectors push them about, then draw. Effectors are what turn an arrangement into motion
 * graphics, and they were the part nobody could find or read: a bare four-way arrow among
 * the arrangement's marks added one, and the strip that replaced the array's own was
 * fourteen marks with no names. So they are started from what they DO (`effectors.ts`),
 * offered by name on the array's own strip, and edited as every number they are made of,
 * named, at once.
 *
 * The reason it is an object rather than a command is the same reason the blend is: a
 * hundred copies made by a command are a hundred things to fix when you change your
 * mind, and a hundred copies made by a cloner are one. Edit the source, move it,
 * recolour it — the array follows, because the array was never stored.
 *
 * Picking the tool up with something chosen wraps it, because that is the only thing
 * anybody means by pointing a cloner at a selection, and a tool that waits to be told
 * again is a tool that looks broken.
 */

/**
 * Every label starts with the NAME.
 *
 * A tooltip that only describes leaves you no way to talk about what you just used, or
 * to look it up, or to recognise it in anybody else's editor. The name is the part that
 * has to survive; the sentence after it is what the name means.
 */
const MODES: { id: ClonerMode; icon: IconName; title: string }[] = [
  { id: 'grid', icon: 'cloner', title: 'Grid — a copy in every cell, so many across and so many down' },
  { id: 'linear', icon: 'cloner-linear', title: 'Linear — a run of copies, each one further along than the last' },
  { id: 'radial', icon: 'cloner-radial', title: 'Radial — copies spaced round a circle' },
  { id: 'path', icon: 'text-path', title: 'On a path — copies strung along a shape you drew; click one to choose it' },
];

/** How near the handle counts as being, in screen pixels. */
const GRIP = 9;
/**
 * How many copies the overlay marks, at most.
 *
 * Every mark is a drawing call, and drawing calls are what make a frame late — four
 * hundred of them cost forty milliseconds, which is the whole frame budget spent on a
 * hint about an arrangement the copies themselves are already showing. Past this many,
 * it marks a spread of them rather than all of them.
 */
const MOST_MARKS = 64;

/** 1st, 2nd, 3rd, 4th — and 11th, 21st, 31st, which the obvious version gets wrong. */
function ordinal(value: number): string {
  const tens = value % 100;
  if (tens >= 11 && tens <= 13) return `${value}th`;
  const ones = value % 10;
  return `${value}${ones === 1 ? 'st' : ones === 2 ? 'nd' : ones === 3 ? 'rd' : 'th'}`;
}

export function clonerTool(): Tool {
  // Keep standard selection, move, resize, rotation and undo behavior in this tool.
  // The repeat's own handles (how many, how far apart, how big) are Select's, drawn and
  // pulled the same under both tools (`repeatHandles.ts`); this tool adds the effectors'.
  const selection = selectTool();
  /** An effector being moved about the page, which is how a field is aimed. */
  let pushing: { id: string; from: Point; was: [number, number] } | null = null;
  /** An effector being resized, which is how far a field reaches. */
  let sizing: { id: string; from: Point; was: { width: number; height: number } } | null = null;
  /**
   * The slider last turned — which number on which node, and when — so that one drag of
   * it is one undo step and not one per tick, nor one merged with whatever came before.
   * See `slide`.
   */
  let slid: { key: string; at: number } | null = null;

  /** The cloner being worked on: one that is chosen, or the one holding what is. */
  const chosen = (ctx: ToolContext): string | null => {
    for (const id of ctx.selection) {
      if (ctx.node(id)?.type === 'cloner') return id;
      const parent = ctx.scene.parentOf(id);
      if (parent && ctx.node(parent)?.type === 'cloner') return parent;
    }
    return null;
  };

  /** The effector being worked on, if one is chosen. */
  const chosenEffector = (ctx: ToolContext): string | null =>
    ctx.selection.find((id) => ctx.node(id)?.type === 'effector') ?? null;

  /** The cloner an effector belongs to, so its rail can still reach the array. */
  const ownerOf = (ctx: ToolContext, effectorId: string): string | null => findOwner(ctx, effectorId);

  /** A node's outline in page coordinates, for drawing a shape field where it stands. */
  const ringsOf = (ctx: ToolContext, id: string): Point[][] => {
    const node = ctx.node(id);
    if (!node) return [];
    const cmds = outlineIn(ctx.scene, node, ctx.scene.worldTransform(id));
    if (!cmds) return [];
    return subpathsOfCommands(cmds)
      .map((subpath) => flattened(subpath, 12))
      .filter((ring) => ring.length >= 2);
  };

  /** The far corner of a field, which is the handle that says how far it reaches. */
  const cornerOf = (ctx: ToolContext, id: string): Point | null => {
    const node = ctx.node(id);
    if (node?.type !== 'effector' || node.field === 'none' || node.field === 'shape') return null;
    return applyToPoint(ctx.scene.worldTransform(id), node.width, node.height);
  };

  /** Where an effector sits on the page: the middle of its field, which is its handle. */
  const middleOf = (ctx: ToolContext, id: string): Point | null => {
    const node = ctx.node(id);
    if (node?.type !== 'effector') return null;
    return applyToPoint(ctx.scene.worldTransform(id), node.width / 2, node.height / 2);
  };

  /**
   * Which of an effector's two handles the pointer is over, for the cursor to say so.
   *
   * The same reach as a press: the corner of the one chosen is the size, and the middle of
   * any of its cloner's is the place. Without a cursor the only way to learn that a small
   * square can be taken hold of was to press on it by accident.
   */
  const effectorHandleAt = (ctx: ToolContext, world: Point): 'size' | 'move' | null => {
    const reach = GRIP * ctx.unitsPerPixel;
    const chosenOne = chosenEffector(ctx);
    if (chosenOne) {
      const corner = cornerOf(ctx, chosenOne);
      if (corner && Math.hypot(corner.x - world.x, corner.y - world.y) <= reach) return 'size';
    }
    const owner = chosenOne ? ownerOf(ctx, chosenOne) : null;
    const id = chosen(ctx);
    const handles = owner ? clonerEffectors(ctx.scene, owner) : chosenOne ? [chosenOne] : id ? clonerEffectors(ctx.scene, id) : [];
    for (const each of handles) {
      const middle = middleOf(ctx, each);
      if (middle && Math.hypot(middle.x - world.x, middle.y - world.y) <= reach) return 'move';
    }
    return null;
  };

  // What Repeat, Make Layers, Stop Repeating and a variation do lives in ./repeat, which
  // the Edit dock's Repeat popover calls too. Each resets the slider's step first.
  const wrap = (ctx: ToolContext): void => {
    slid = null;
    wrapInCloner(ctx);
  };
  const expand = (ctx: ToolContext, id: string): void => {
    slid = null;
    if (makeLayers(ctx, id)) ctx.finish();
  };
  const addEffector = (ctx: ToolContext, id: string, recipe: Recipe): void => {
    slid = null;
    if (!addVariation(ctx, id, recipe)) return;
    ctx.railChanged();
    ctx.refresh();
  };
  const removeEffector = (ctx: ToolContext, effectorId: string): void => {
    slid = null;
    removeVariation(ctx, effectorId);
    ctx.railChanged();
    ctx.refresh();
  };

  /** Chooses an effector, which is how its numbers come up on the strip. */
  const openEffector = (ctx: ToolContext, effectorId: string): void => {
    ctx.stopCapturing();
    slid = null;
    ctx.setSelection([effectorId]);
    ctx.railChanged();
    ctx.refresh();
  };

  const release = (ctx: ToolContext, id: string): void => {
    slid = null;
    stopRepeating(ctx, id);
  };

  /**
   * Writes to a node and says so, WITHOUT closing the undo step.
   *
   * For the moves of a drag on the canvas, which are one step together and are closed
   * when the pointer comes up. Anything from the options bar goes through `setting` or
   * `slide` instead.
   */
  const set = (ctx: ToolContext, id: string, props: Record<string, unknown>): void => {
    ctx.edit([{ op: 'set', id, props }]);
    ctx.railChanged();
    ctx.refresh();
  };

  /**
   * One discrete choice — a mode, Follow, a kind, a field, a shape pointed at — as one
   * undo step of its own, closed on both sides.
   *
   * The undo manager groups nothing by time (its capture lasts a day), so a step ends only
   * where something ends it. The bar's changes used to end nothing: a mode, a count and
   * Follow changed one after another, then Expand, and one Undo took back all four
   * together (QA 2026-09-24, cloner-F2). The blend tool's `setting` does the same.
   */
  const setting = (ctx: ToolContext, id: string, props: Record<string, unknown>): void => {
    ctx.stopCapturing();
    slid = null;
    set(ctx, id, props);
    ctx.stopCapturing();
  };

  /**
   * How long a slider can rest before the next turn of it counts as a new drag.
   *
   * The options bar tells a tool about every value a slider passes through and nothing
   * about where a drag starts or ends, so the end has to be inferred. Longer than the
   * gap between two ticks of the slowest deliberate drag; shorter than letting go and
   * taking hold again to try another value.
   */
  const SLIDE_GAP_MS = 700;

  /**
   * A slider's change: every tick of one drag in ONE undo step (cloner-F2).
   *
   * Closing after every tick, as `setting` does, would make a drag across a slider forty
   * steps to undo one at a time. So the step is closed BEFORE a drag instead — whenever
   * this tick is on a different slider or node than the last one, or comes after a pause —
   * and left open behind it for the next thing to close: a press on the canvas does, and
   * so does every button here. Decided on the tick itself rather than by a timer, because
   * a timer firing in the middle of the next drag would cut that drag's step in two.
   */
  const slide = (ctx: ToolContext, id: string, props: Record<string, unknown>): void => {
    const key = `${id}\u0000${Object.keys(props).join(',')}`;
    const now = performance.now();
    if (!slid || slid.key !== key || now - slid.at > SLIDE_GAP_MS) ctx.stopCapturing();
    slid = { key, at: now };
    set(ctx, id, props);
  };

  return {
    id: 'cloner',

    activate(ctx) {
      emitHintSignal('cloner.used');
      // Picking the tool up changes nothing on the page. It used to wrap whatever was
      // chosen on the spot (a 4 × 3 grid at 1.25× — a 600 px rectangle became three
      // artboards wide), which is a tool pick restructuring the design (audit, 15 Oct).
      // The strip's Repeat button, the selection bar and the Repeat popover wrap on a
      // press; `wrap` is the one place they all go through.
      ctx.refresh();
    },

    deactivate(ctx) {
      selection.deactivate?.(ctx);
      // A handle let go of by a tool change rather than a release is still a finished
      // drag: close its step, or it merged into whatever was done next and one Undo took
      // back both (QA 2026-09-24, cloner-F19).
      if (pushing || sizing) ctx.stopCapturing();
      pushing = null;
      sizing = null;
      slid = null;
      ctx.setCursor(null);
      ctx.refresh();
    },

    // A handle drag is one step until it ends, so Escape during it is not a new command:
    // the step stays open for `onKeyDown` to put the numbers back inside it (cloner-F19).
    holdsStep: () => !!(pushing || sizing || selection.holdsStep?.()),

    onKeyDown(event, ctx) {
      if (selection.onKeyDown?.(event, ctx)) return true;
      // Escape mid-drag puts the handle back where it was taken from. It used to reach
      // only the canvas, which cleared the selection while the drag went on editing the
      // cloner it had let go of (QA 2026-09-24, cloner-F19).
      if (event.key !== 'Escape' || !(pushing || sizing)) return false;
      event.preventDefault();
      if (sizing) set(ctx, sizing.id, { ...sizing.was });
      if (pushing) {
        const node = ctx.node(pushing.id);
        if (node?.type === 'effector') {
          const back = [...node.transform] as [number, number, number, number, number, number];
          back[4] = pushing.was[0];
          back[5] = pushing.was[1];
          set(ctx, pushing.id, { transform: back });
        }
      }
      pushing = null;
      sizing = null;
      ctx.stopCapturing();
      ctx.setCursor(null);
      ctx.refresh();
      return true;
    },

    onPointerDown(event, world, ctx) {
      // The canvas has closed the undo step for this press; a slider turned after it is
      // a new drag whatever the clock says.
      slid = null;
      const chosenOne = chosenEffector(ctx);
      /**
       * The handles first, and the two "point at a shape" pickers only after them.
       *
       * The pickers used to come first, and a press on an effector's own dot while its
       * field was a Shape re-picked the shape from whatever lay under the dot — the cloner,
       * mostly, which has no outline, so the effect switched off; the dot could only be
       * taken hold of over empty canvas. A path cloner did the same with its path
       * (QA 2026-09-24, cloner-F7). A handle is the smallest thing on screen and the one a
       * press near it is aimed at.
       */
      // The corner handle before the middle one: it is the outer of the two and a press
      // near it means the size, not the place.
      if (chosenOne) {
        const corner = cornerOf(ctx, chosenOne);
        const node = ctx.node(chosenOne);
        if (corner && node?.type === 'effector' && Math.hypot(corner.x - world.x, corner.y - world.y) <= GRIP * ctx.unitsPerPixel) {
          sizing = { id: chosenOne, from: world, was: { width: node.width, height: node.height } };
          return true;
        }
      }
      // An effector's handle: the chosen one's owner's, or the chosen cloner's.
      const owner = chosenOne ? ownerOf(ctx, chosenOne) : null;
      const id = chosen(ctx);
      const handles = owner ? clonerEffectors(ctx.scene, owner) : chosenOne ? [chosenOne] : id ? clonerEffectors(ctx.scene, id) : [];
      for (const each of handles) {
        const middle = middleOf(ctx, each);
        const node = ctx.node(each);
        if (!middle || node?.type !== 'effector') continue;
        if (Math.hypot(middle.x - world.x, middle.y - world.y) > GRIP * ctx.unitsPerPixel) continue;
        pushing = { id: each, from: world, was: [node.transform[4], node.transform[5]] };
        ctx.setSelection([each]);
        ctx.refresh();
        return true;
      }
      // A path arrangement needs to be told WHICH path, and pointing at one is the only
      // sensible way to say it. Not the cloner or anything in it, and not an effector,
      // which has no outline to string anything along.
      const riding = id ? ctx.node(id) : null;
      if (id && riding?.type === 'cloner' && riding.mode === 'path') {
        const hit = ctx.hit(world);
        if (hit && hit !== id && !ctx.scene.isAncestor(id, hit) && ctx.node(hit)?.type !== 'effector') {
          setting(ctx, id, { pathId: hit });
          return true;
        }
      }
      // A field made of a drawn shape needs to be told WHICH shape, and the only sensible
      // way to say that is to point at one. Skipping, as the path picker does, the cloner
      // it pushes and what is inside it — pressing over the array is not choosing a field —
      // and anything with no outline, which as a field reaches nothing (cloner-F7).
      const chosenNode = chosenOne ? ctx.node(chosenOne) : null;
      if (chosenOne && chosenNode?.type === 'effector' && chosenNode.field === 'shape') {
        const hit = ctx.hit(world);
        const target = hit ? ctx.node(hit) : null;
        const inArray = !!owner && !!hit && (hit === owner || ctx.scene.isAncestor(owner, hit));
        if (hit && target && hit !== chosenOne && !inArray && outlineIn(ctx.scene, target, ctx.scene.worldTransform(hit))) {
          setting(ctx, chosenOne, { shapeId: hit });
          return true;
        }
      }
      return selection.onPointerDown?.(event, world, ctx);
    },

    onDoubleClick(event, world, ctx) {
      return selection.onDoubleClick?.(event, world, ctx);
    },

    onPointerMove(event, world, ctx) {
      if (selection.holdsStep?.()) {
        selection.onPointerMove?.(event, world, ctx);
        return;
      }
      if (sizing && event.buttons !== 0) {
        const node = ctx.node(sizing.id);
        if (node?.type !== 'effector') return;
        // Measured from the effector's own origin, so the corner lands under the pointer
        // however the field has been turned.
        const back = ctx.scene.worldTransform(sizing.id);
        const origin = applyToPoint(back, 0, 0);
        const reach = applyToPoint(back, 1, 0);
        const unit = Math.hypot(reach.x - origin.x, reach.y - origin.y) || 1;
        set(ctx, sizing.id, {
          width: Math.max(4, Math.round(sizing.was.width + (world.x - sizing.from.x) / unit)),
          height: Math.max(4, Math.round(sizing.was.height + (world.y - sizing.from.y) / unit)),
        });
        return;
      }
      if (pushing && event.buttons !== 0) {
        const node = ctx.node(pushing.id);
        if (node?.type !== 'effector') return;
        const next = [...node.transform] as [number, number, number, number, number, number];
        next[4] = pushing.was[0] + (world.x - pushing.from.x);
        next[5] = pushing.was[1] + (world.y - pushing.from.y);
        ctx.edit([{ op: 'set', id: pushing.id, props: { transform: next } }]);
        ctx.refresh();
        return;
      }
      if (event.buttons === 0) {
        const grabbed = effectorHandleAt(ctx, world);
        if (grabbed) {
          ctx.setCursor(grabbed === 'size' ? 'nwse-resize' : 'move');
          return;
        }
      }
      selection.onPointerMove?.(event, world, ctx);
    },

    onPointerUp(event, world, ctx) {
      if (selection.holdsStep?.()) {
        selection.onPointerUp?.(event, world, ctx);
        return;
      }
      if (sizing) {
        sizing = null;
        ctx.stopCapturing();
        ctx.refresh();
        return;
      }
      if (pushing) {
        pushing = null;
        ctx.stopCapturing();
        ctx.refresh();
      }
    },

    rail(ctx) {
      // An effector chosen means the rail is about the effector: it is the thing in hand,
      // and its numbers are the ones being turned.
      const effectorId = chosenEffector(ctx);
      const effector = effectorId ? ctx.node(effectorId) : null;
      if (effectorId && effector?.type === 'effector') {
        const owner = ownerOf(ctx, effectorId);
        const name = effector.name || 'Effector';
        const valueOf = (field: (typeof CHANNELS)[number]['field']) => (effector as unknown as Record<string, number>)[field] ?? 0;
        // Every amount, named, at once. The first version had one slider and a row of marks
        // to say which amount it was turning, which hid which of them were set — the only
        // way to find out what an effector did was to click through all five.
        const fields: RailField[] = CHANNELS.map((channel) => ({
          id: channel.id,
          label: channel.label,
          title: channel.title,
          value: valueOf(channel.field),
          min: channel.min,
          max: channel.max,
          step: channel.step,
          format: (value: number) => channel.format(value, ctx.units),
          parse: (text: string) => channel.parse(text, ctx.units),
          onChange: (value: number) => slide(ctx, effectorId, { [channel.field]: channel.round(value) }),
        }));
        const how = applyOf(effector.kind);
        const where = whereOf(effector.field);
        const choose = (props: Record<string, unknown>) => () => setting(ctx, effectorId, props);
        const applyItems: MenuEntry[] = APPLY.map((one) => ({ id: one.id, label: one.label, icon: one.icon, checked: how.id === one.id, onSelect: choose({ kind: one.id }) }));
        const whereItems: MenuEntry[] = WHERE.map((one) => ({ id: one.id, label: one.label, icon: one.icon, checked: where.id === one.id, onSelect: choose({ field: one.id }) }));
        // Only what applies. Reversing a field is meaningless without one, and shuffling is
        // meaningless unless the amounts are random; a button that does nothing here is
        // one more thing to read, so it is left out rather than greyed.
        const switches: RailButton[] = [
          ...(effector.field !== 'none'
            ? [
                {
                  id: 'reverse',
                  icon: 'invert' as IconName,
                  label: 'Reverse',
                  title: 'Reverse — swap which copies get the most: the ones in the middle or the ones out at the edge',
                  pressed: !!effector.invert,
                  run: () => setting(ctx, effectorId, { invert: !effector.invert }),
                },
              ]
            : []),
          ...(effector.kind === 'random'
            ? [
                {
                  id: 'shuffle',
                  icon: 'effector-random' as IconName,
                  label: 'Shuffle',
                  title: 'Shuffle — a different random spread. Each one stays the same every time the file is opened',
                  // Any other whole number: what it is is only that it is not the last one.
                  run: () => setting(ctx, effectorId, { seed: 1 + Math.floor(Math.random() * 2 ** 30) }),
                },
              ]
            : []),
          {
            id: 'remove',
            icon: 'trash' as IconName,
            label: 'Remove',
            title: 'Remove — take this effector away; the copies go back as they were',
            run: () => removeEffector(ctx, effectorId),
          },
        ];
        return {
          title: name,
          // Where you are, and the way back: the array this pushes, then this effector's name.
          top: {
            kind: 'group' as const,
            wells: [
              ...(owner
                ? [
                    {
                      kind: 'buttons' as const,
                      items: [
                        {
                          id: 'array',
                          icon: 'back' as IconName,
                          label: 'Repeat',
                          title: 'Repeat — back to the array this pushes about',
                          run: () => {
                            ctx.stopCapturing();
                            slid = null;
                            ctx.setSelection([owner]);
                            ctx.railChanged();
                            ctx.refresh();
                          },
                        },
                      ],
                    },
                  ]
                : []),
              { kind: 'text' as const, text: name },
            ],
          },
          middle: { kind: 'fields' as const, items: fields },
          bottom: {
            kind: 'group' as const,
            wells: [
              { kind: 'break' as const },
              { kind: 'menu' as const, label: 'Apply', title: 'Apply — how the amounts are handed out across the copies', value: how.label, items: applyItems },
              { kind: 'menu' as const, label: 'Where', title: 'Where — which copies feel it: all of them, or the ones near a spot, a box, a side or a shape', value: where.label, items: whereItems },
              { kind: 'buttons' as const, items: switches },
            ],
          },
        };
      }
      const id = chosen(ctx);
      const node = id ? ctx.node(id) : null;
      const live = node?.type === 'cloner' ? node : null;
      if (!live || !id) {
        return {
          top: {
            kind: 'buttons' as const,
            items: [
              {
                id: 'make',
                icon: 'cloner' as IconName,
                title: 'Repeat — make copies of what is chosen',
                disabled: ctx.selection.length === 0,
                run: () => wrap(ctx),
              },
            ] as RailButton[],
          },
        };
      }

      /**
       * The three arrangements, then the two ways out of being an arrangement.
       *
       * They share a row because the rail has three wells and a cloner has two numbers
       * per mode to fill them with. The alternative was leaving one of those numbers
       * unreachable, and a setting nobody can get at is worse than a row that has to be
       * read rather than glanced at — which is what the titles are for.
       */
      const top: Well = {
        kind: 'buttons',
        items: [
          ...MODES.map((item) => ({
            id: item.id,
            icon: item.icon,
            title: item.title,
            pressed: live.mode === item.id,
            group: 'Arrangement',
            run: () => setting(ctx, id, { mode: item.id }),
          })),
          ...(live.mode === 'path'
            ? [
                {
                  id: 'follow',
                  icon: 'rotate' as IconName,
                  title: live.follow
                    ? 'Following the path — each copy turns with it. Press to leave them upright'
                    : 'Upright — each copy keeps its heading. Press to turn them with the path',
                  pressed: live.follow,
                  run: () => setting(ctx, id, { follow: !live.follow }),
                },
              ]
            : []),
          { id: 'expand', icon: 'expand' as IconName, title: 'Expand — turn this array into ordinary layers', run: () => expand(ctx, id) },
          { id: 'release', icon: 'release' as IconName, title: 'Release — take the array away and keep what it was copying', run: () => release(ctx, id) },
        ] as RailButton[],
      };

      const middle: Well =
        live.mode === 'path'
          ? {
              kind: 'slider',
              icon: 'text-path',
              title: 'Count — how many copies are strung along it',
              value: live.count,
              min: 1,
              max: 200,
              step: 1,
              onChange: (value) => slide(ctx, id, { count: Math.round(value) }),
            }
          : live.mode === 'grid'
          ? {
              kind: 'slider',
              icon: 'cloner',
              title: 'Columns — how many copies across',
              value: live.countX,
              min: 1,
              max: 60,
              step: 1,
              onChange: (value) => slide(ctx, id, { countX: Math.round(value) }),
            }
          : {
              kind: 'slider',
              icon: 'cloner-linear',
              title: 'Count — how many copies',
              value: live.count,
              min: 1,
              max: 200,
              step: 1,
              onChange: (value) => slide(ctx, id, { count: Math.round(value) }),
            };

      const bottom: Well =
        live.mode === 'path'
          ? {
              kind: 'slider',
              icon: 'spacing',
              title: 'Spread — how much of the path the run covers',
              value: live.spread,
              min: -1,
              max: 1,
              step: 0.01,
              format: (value) => `${Math.round(value * 100)}%`,
              parse: percentIn,
              onChange: (value) => slide(ctx, id, { spread: Number(value.toFixed(2)) }),
            }
          : live.mode === 'grid'
          ? {
              kind: 'slider',
              icon: 'cloner',
              title: 'Rows — how many copies down',
              value: live.countY,
              min: 1,
              max: 60,
              step: 1,
              onChange: (value) => slide(ctx, id, { countY: Math.round(value) }),
            }
          : live.mode === 'radial'
            ? {
                // How far ROUND, not how far out: the handle already sets the radius by
                // dragging, and a part ring is the thing a slider cannot be done without.
                kind: 'slider',
                icon: 'cloner-radial',
                title: 'Sweep — how far round the ring the copies go',
                value: live.endAngle,
                min: -360,
                max: 360,
                step: 1,
                format: (value) => `${Math.round(value)}°`,
                parse: numberIn,
                onChange: (value) => slide(ctx, id, { endAngle: Math.round(value) }),
              }
            : {
                // Likewise: the handle sets how far apart, so the slider spends itself on
                // the turn, which is what makes a run of copies into a fan or a spiral.
                kind: 'slider',
                icon: 'cloner-radial',
                title: 'Twist — how much further each copy is turned than the last',
                value: live.stepRotation,
                min: -180,
                max: 180,
                step: 1,
                format: (value) => `${Math.round(value)}°`,
                parse: numberIn,
                onChange: (value) => slide(ctx, id, { stepRotation: Math.round(value) }),
              };

      // Effectors, by name, on the array's own strip. They used to be one unlabelled mark
      // among the arrangement's, and nobody who had not been told found it. With none, the
      // one thing to do is add; with some, each is a button that opens it, and the plus is
      // still there for another.
      const effectorIds = clonerEffectors(ctx.scene, id);
      // A long hover on a row shows what it does to an array (editor/guides/demos/effectors).
      const starts: MenuEntry[] = RECIPES.map((recipe) => ({ id: recipe.id, label: recipe.name, icon: recipe.icon, guide: `effector:${recipe.id}`, onSelect: () => addEffector(ctx, id, recipe) }));
      const effectorsWell: Well = {
        kind: 'group',
        wells: [
          ...(effectorIds.length > 0
            ? [
                {
                  kind: 'buttons' as const,
                  items: effectorIds.map((each) => {
                    const one = ctx.node(each);
                    const name = one?.name || 'Effector';
                    return {
                      id: `effector:${each}`,
                      icon: applyOf(one?.type === 'effector' ? one.kind : undefined).icon,
                      label: name,
                      bare: true,
                      title: `${name} — change what it does`,
                      run: () => openEffector(ctx, each),
                    };
                  }),
                },
              ]
            : []),
          {
            kind: 'menu' as const,
            action: true,
            icon: 'plus' as IconName,
            value: effectorIds.length > 0 ? '' : 'Effector',
            title: 'Add Effector — push the copies about: scatter them, grow them, twist them, swell the ones in the middle',
            items: starts,
          },
        ],
      };

      return { top, middle, bottom: { kind: 'group', wells: [bottom, effectorsWell] } };
    },

    draw(overlay: Overlay, ctx: ToolContext) {
      if (!chosenEffector(ctx)) selection.draw?.(overlay, ctx);
      const id = chosen(ctx) ?? (chosenEffector(ctx) ? ownerOf(ctx, chosenEffector(ctx)!) : null);
      // The fields, whichever of the two is chosen: an effector is only legible next to
      // the array it is pushing, and the array is only explicable next to the field.
      const showing = id ? clonerEffectors(ctx.scene, id) : chosenEffector(ctx) ? [chosenEffector(ctx)!] : [];
      for (const each of showing) {
        const node = ctx.node(each);
        if (node?.type !== 'effector') continue;
        const world = ctx.scene.worldTransform(each);
        const corner = (x: number, y: number) => applyToPoint(world, x, y);
        // The wash says where the effect is strong, which is the part of a field that is
        // hard to picture from its outline alone: a few translucent layers, each one
        // smaller than the last, so the middle of it is the deepest. Only for the effector
        // being worked on, and not for a reversed one, whose strong place is the outside.
        const washed = chosenEffector(ctx) === each && !node.invert;
        const WASH = 0.045;
        const layers = [1, 2 / 3, 1 / 3];
        if (node.field === 'linear' || node.field === 'box') {
          const ring = [corner(0, 0), corner(node.width, 0), corner(node.width, node.height), corner(0, node.height)];
          overlay.curve([...ring, ring[0]!].map((at) => ({ at })), { width: 1 });
          if (node.field === 'linear') {
            // Which way it falls: from the full edge towards the empty one.
            overlay.line(corner(0, node.height / 2), corner(node.width, node.height / 2), { width: 1, alpha: 0.6 });
            if (washed) {
              for (const share of layers) {
                overlay.region([[corner(0, 0), corner(node.width * share, 0), corner(node.width * share, node.height), corner(0, node.height)]], { alpha: WASH });
              }
            }
          } else {
            // A box field is strongest in the middle, so the middle is marked — otherwise
            // it is the same rectangle as a linear one and says nothing different.
            const half = [corner(node.width / 4, node.height / 4), corner((node.width * 3) / 4, node.height / 4), corner((node.width * 3) / 4, (node.height * 3) / 4), corner(node.width / 4, (node.height * 3) / 4)];
            overlay.curve([...half, half[0]!].map((at) => ({ at })), { width: 1 });
            if (washed) {
              for (const share of layers) {
                const inset = ((1 - share) / 2);
                overlay.region([[
                  corner(node.width * inset, node.height * inset),
                  corner(node.width * (1 - inset), node.height * inset),
                  corner(node.width * (1 - inset), node.height * (1 - inset)),
                  corner(node.width * inset, node.height * (1 - inset)),
                ]], { alpha: WASH });
              }
            }
          }
        } else if (node.field === 'shape') {
          // The shape doing the work, outlined where it stands. A field you cannot see is
          // a field you cannot aim.
          const rings = node.shapeId ? ringsOf(ctx, node.shapeId) : [];
          for (const loop of rings) overlay.curve([...loop, loop[0]!].map((at) => ({ at })), { width: 1.5 });
          if (washed && rings.length > 0) overlay.region(rings, { alpha: WASH * 1.5 });
          if (rings.length === 0 && chosenEffector(ctx) === each) {
            const middle = middleOf(ctx, each);
            if (middle) overlay.label(middle, 'click a shape to use as the field', { align: 'center', baseline: 'top', offset: { x: 0, y: 14 } });
          }
        } else if (node.field === 'radial') {
          const rx = node.width / 2;
          const ry = node.height / 2;
          const around = (share: number): Point[] => {
            const out: Point[] = [];
            for (let step = 0; step < 48; step++) {
              const turn = (step / 48) * Math.PI * 2;
              out.push(corner(rx + Math.cos(turn) * rx * share, ry + Math.sin(turn) * ry * share));
            }
            return out;
          };
          const ring = around(1);
          overlay.curve([...ring, ring[0]!].map((at) => ({ at })), { width: 1 });
          if (washed) for (const share of layers) overlay.region([around(share)], { alpha: WASH });
        }
        // The corner says how far it reaches, and dragging it is how that is changed.
        const reach = cornerOf(ctx, each);
        if (reach && chosenEffector(ctx) === each) overlay.handle(reach, { filled: true, size: 4 });
        const middle = middleOf(ctx, each);
        if (middle) {
          overlay.handle(middle, { filled: chosenEffector(ctx) === each, size: 5.5 });
          if (chosenEffector(ctx) === each) {
            overlay.label(middle, node.name || 'Effector', { align: 'center', baseline: 'bottom', offset: { x: 0, y: -12 }, tone: 'quiet' });
          }
        }
      }
      if (!id) return;
      const node = ctx.node(id);
      if (node?.type !== 'cloner') return;

      // The path the copies are riding, outlined where it stands — and said plainly when
      // there is not one yet, because an arrangement that draws nothing and explains
      // nothing is indistinguishable from a broken one.
      if (node.mode === 'path') {
        const rings = node.pathId ? ringsOf(ctx, node.pathId) : [];
        for (const loop of rings) overlay.curve(loop.map((at) => ({ at })), { width: 1.5 });
        if (rings.length === 0) {
          const where = ctx.boundsOf(id);
          if (where) {
            overlay.label(
              { x: where.x + where.width / 2, y: where.y + where.height / 2 },
              'click a shape to string the copies along it',
              { align: 'center', baseline: 'middle' },
            );
          }
        }
      }

      const spread = clonerPlacements(ctx.scene, node);
      const world = ctx.scene.worldTransform(id);
      // A mark where the copies sit, so the arrangement reads even where they overlap
      // into one another. Strided rather than truncated past the cap, so what is marked
      // is the whole arrangement thinned out and not the first corner of it.
      const stride = Math.max(1, Math.ceil(spread.length / MOST_MARKS));
      let marked = 0;
      for (let at = 0; at < spread.length; at += stride) {
        const spot = applyToPoint(spread[at]!.transform, 0, 0);
        overlay.handle(applyToPoint(world, spot.x, spot.y), { size: 2 });
        marked += 1;
      }

      const total = cloneCount(clonerSpec(node));
      const box = ctx.boundsOf(id);
      if (!box) return;
      const said = `${total} ${total === 1 ? 'copy' : 'copies'}${total > marked ? ` · every ${ordinal(stride)} marked` : ''}`;
      overlay.label({ x: box.x + box.width / 2, y: box.y }, said, { align: 'center', baseline: 'bottom', offset: { x: 0, y: -10 } });
    },
  };
}
